/*
 * mk-collection.js — Meher Kakalia collection page enhancements.
 *
 *  1. Scroll-reveal: fades product cards in as they enter the viewport.
 *  2. Pagination pruning: the collection paginates `collection.products`, whose
 *     count includes products whose variant for the active size/option filter is
 *     SOLD OUT. mk-product-color-cards.liquid hides those cards, so a paginated
 *     page can claim to exist (Shopify count > products_per_page) yet render zero
 *     real cards — leaving a phantom "1 2" pagination when only one page has
 *     results. Liquid can't see other pages from page 1, so we verify the claimed
 *     pages client-side: prefetch each, count real <product-card> elements, and
 *     drop links to empty pages (removing the nav entirely if one real page is left).
 *
 *  Both behaviours re-initialise after facet AJAX: theme.js swaps the section via
 *  replaceChildren WITHOUT firing shopify:section:load, so a MutationObserver on the
 *  section re-runs them against the freshly-rendered DOM.
 */
(function () {
  "use strict";

  function initReveal(section) {
    if (!("IntersectionObserver" in window)) return;
    if (section.__mkRevealObserver) {
      section.__mkRevealObserver.disconnect();
    }
    var grids = section.querySelectorAll("[data-mk-reveal]");
    if (!grids.length) return;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -60px 0px", threshold: 0.08 }
    );
    section.__mkRevealObserver = observer;
    grids.forEach(function (grid) {
      Array.prototype.forEach.call(grid.children, function (child) {
        observer.observe(child);
      });
    });
  }

  // Remove a pagination item along with its facet-link wrapper (if any).
  function removeItem(el) {
    var wrapper = el.closest ? el.closest("facet-link") : null;
    var target = wrapper || el;
    if (target.parentNode) target.parentNode.removeChild(target);
  }

  function prunePagination(section) {
    // Only act on filtered views — that's the only time the availability gate
    // can desync the rendered cards from Shopify's paginated count.
    var params = new URLSearchParams(window.location.search);
    var hasFilter = false;
    params.forEach(function (value, key) {
      if (key.indexOf("filter.") === 0) hasFilter = true;
    });
    if (!hasFilter) return;

    var nav = section.querySelector(".pagination");
    if (!nav || nav.__mkPruned) return;
    nav.__mkPruned = true; // guard against the MutationObserver re-entering on our own edits

    var list = section.querySelector(".product-list");
    if (!list) return;

    var currentPage = parseInt(params.get("page"), 10) || 1;
    var currentReal = list.querySelectorAll("product-card").length;

    // Collect the numbered page links (skip the prev/next arrows).
    var anchors = Array.prototype.slice.call(nav.querySelectorAll("a.pagination__link"));
    var pageLinks = [];
    var urlByNum = {};
    anchors.forEach(function (a) {
      if (a.rel === "prev" || a.rel === "next") return;
      var num = parseInt((a.textContent || "").trim(), 10);
      if (!num) return;
      var url = a.getAttribute("href");
      pageLinks.push({ num: num, url: url });
      urlByNum[num] = url;
    });
    if (!pageLinks.length) return;

    // Fetch every other claimed page and count the cards it actually renders.
    Promise.all(
      pageLinks.map(function (p) {
        return fetch(p.url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
          .then(function (r) {
            return r.ok ? r.text() : "";
          })
          .then(function (html) {
            var real = true; // fail safe: keep the page if we can't read it
            if (html) {
              var doc = new DOMParser().parseFromString(html, "text/html");
              var pl = doc.querySelector(".shopify-section--main-collection .product-list");
              real = !!pl && pl.querySelectorAll("product-card").length > 0;
            }
            return { num: p.num, real: real };
          })
          .catch(function () {
            return { num: p.num, real: true };
          });
      })
    ).then(function (results) {
      var realSet = {};
      realSet[currentPage] = currentReal > 0;
      results.forEach(function (r) {
        realSet[r.num] = r.real;
      });

      var realPages = Object.keys(realSet)
        .filter(function (k) {
          return realSet[k];
        })
        .map(Number)
        .sort(function (a, b) {
          return a - b;
        });

      // One real page (or none) → the pagination is entirely spurious.
      if (realPages.length <= 1) {
        if (nav.parentNode) nav.parentNode.removeChild(nav);
        return;
      }

      // Drop the numbered links/spans pointing at empty pages.
      var items = Array.prototype.slice.call(nav.querySelectorAll(".pagination__link"));
      items.forEach(function (item) {
        if (item.rel === "prev" || item.rel === "next") return;
        var num = parseInt((item.textContent || "").trim(), 10);
        if (!num || realSet[num]) return;
        removeItem(item);
      });

      // Repoint or remove the prev/next arrows to the nearest surviving page.
      var nextReal = realPages.filter(function (n) {
        return n > currentPage;
      })[0];
      var prevCandidates = realPages.filter(function (n) {
        return n < currentPage;
      });
      var prevReal = prevCandidates.length ? prevCandidates[prevCandidates.length - 1] : null;

      var nextLink = nav.querySelector('a[rel="next"]');
      if (nextLink) {
        if (nextReal) {
          nextLink.setAttribute("href", urlByNum[nextReal]);
        } else {
          removeItem(nextLink);
        }
      }

      var prevLink = nav.querySelector('a[rel="prev"]');
      if (prevLink) {
        if (prevReal) {
          prevLink.setAttribute("href", urlByNum[prevReal]);
        } else {
          removeItem(prevLink);
        }
      }
    });
  }

  function refresh(section) {
    initReveal(section);
    prunePagination(section);
  }

  function watchSection(section) {
    if (!section) return;
    refresh(section);
    if (section.__mkRevealWatched) return;
    section.__mkRevealWatched = true;
    var scheduled = false;
    var raf =
      window.requestAnimationFrame ||
      function (cb) {
        return setTimeout(cb, 16);
      };
    var mo = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      raf(function () {
        scheduled = false;
        refresh(section);
      });
    });
    mo.observe(section, { childList: true });
  }

  function boot() {
    Array.prototype.forEach.call(
      document.querySelectorAll(".shopify-section--main-collection"),
      watchSection
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", function (event) {
    if (event.target && event.target.classList.contains("shopify-section--main-collection")) {
      watchSection(event.target);
    }
  });
})();
